
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Download, Calendar } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfDay, endOfDay, eachDayOfInterval, isSameDay, subDays } from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { exportAllGuardsShiftsToExcel } from '../services/exportService';
import { generateEventColor } from '../../scheduling/utils/colorUtils';
import { useTheme } from '@/contexts/ThemeContext';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { toast } from 'sonner';

interface TeamSchedule {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  team_id: string;
  team_name: string;
  assigned_guards: string[];
}

interface Guard {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
}

interface CustomDateGuardViewProps {
  currentMonth: Date;
}

export const CustomDateGuardView: React.FC<CustomDateGuardViewProps> = ({ currentMonth }) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState<[Date | undefined, Date | undefined]>([
    startOfDay(new Date()),
    endOfDay(new Date())
  ]);
  const [isExporting, setIsExporting] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const startDate = dateRange[0] || startOfDay(new Date());
  const endDate = dateRange[1] || endOfDay(new Date());

  // Generate days in the selected date range
  const selectedDays = eachDayOfInterval({
    start: startOfDay(startDate),
    end: endOfDay(endDate)
  });

  // Fetch guards data
  const { data: guards = [] } = useQuery({
    queryKey: ['guards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .eq('status', 'active');
      
      if (error) throw error;
      return data as Guard[];
    }
  });

  // Fetch schedules for the selected date range (only by start date)
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['schedules-custom-range', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_schedules')
        .select(`
          id,
          title,
          start_date,
          end_date,
          assigned_guards,
          team_id,
          teams!inner(name)
        `)
        .gte('start_date', format(startOfDay(startDate), 'yyyy-MM-dd HH:mm:ss'))
        .lte('start_date', format(endOfDay(endDate), 'yyyy-MM-dd HH:mm:ss'));
      
      if (error) throw error;
      
      return (data || []).map(schedule => ({
        ...schedule,
        team_name: schedule.teams?.name || 'Unknown Team'
      })) as TeamSchedule[];
    }
  });


  const getGuardSchedulesForDay = (guardId: string, date: Date) => {
    return schedules.filter(schedule => {
      if (!schedule.assigned_guards?.includes(guardId)) return false;
      const scheduleStart = fromLocalISOString(schedule.start_date);
      // Only show schedule if it starts on this specific day
      return isSameDay(scheduleStart, date);
    });
  };

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), 'HH:mm');
  };

  const formatGuardName = (guard: Guard) => {
    return guard.full_name || `${guard.first_name} ${guard.last_name}`.trim();
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await exportAllGuardsShiftsToExcel(startDate, endDate);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (contentRef.current) {
      contentRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  // Filter guards that have at least one shift in the selected date range
  const guardsWithShifts = guards.filter(guard => {
    return selectedDays.some(day => getGuardSchedulesForDay(guard.id, day).length > 0);
  });

  // Helper function to find previous day's work for a guard
  const findPreviousDayWork = (guardId: string, targetDate: Date) => {
    const previousDay = subDays(targetDate, 1);
    const previousDayShifts = schedules.filter(schedule => {
      return schedule.assigned_guards?.includes(guardId) && 
             isSameDay(fromLocalISOString(schedule.start_date), previousDay);
    });
    return previousDayShifts.length > 0 ? previousDayShifts[0] : null;
  };

  // Add repo shift based on previous day's work
  const addRepoShift = async (guardId: string, targetDate: Date) => {
    const previousWork = findPreviousDayWork(guardId, targetDate);
    
    if (!previousWork) {
      toast.error(t('noPreviousDayWork') || 'No previous day work found for this guard');
      return;
    }

    try {
      const startDateTime = new Date(targetDate);
       startDateTime.setHours(13, 0, 0, 0);
       
       const endDateTime = new Date(targetDate);
       endDateTime.setHours(13, 1, 0, 0);

      const { error } = await supabase
        .from('team_schedules')
        .insert({
          title: 'ΡΕΠΟ',
          start_date: toLocalISOString(startDateTime),
          end_date: toLocalISOString(endDateTime),
          team_id: previousWork.team_id,
          assigned_guards: [guardId],
          description: `Repo shift based on ${previousWork.title}`,
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (error) throw error;

      toast.success(t('repoShiftCreated') || 'Repo shift created successfully');
      queryClient.invalidateQueries({ queryKey: ['schedules-custom-range'] });
    } catch (error) {
      console.error('Error creating repo shift:', error);
      toast.error(t('errorCreatingRepoShift') || 'Error creating repo shift');
    }
  };

  // Add leave shift function
  const addLeaveShift = async (guardId: string, targetDate: Date, leaveType: string) => {
    const previousWork = findPreviousDayWork(guardId, targetDate);
    
    if (!previousWork) {
      toast.error(t('noPreviousDayWork') || 'No previous day work found for this guard');
      return;
    }

    try {
      const startDateTime = new Date(targetDate);
       startDateTime.setHours(14, 1, 0, 0);
       
       const endDateTime = new Date(targetDate);
       endDateTime.setHours(22, 0, 0, 0);

      const { error } = await supabase
        .from('team_schedules')
        .insert({
          title: leaveType,
          start_date: toLocalISOString(startDateTime),
          end_date: toLocalISOString(endDateTime),
          team_id: previousWork.team_id,
          assigned_guards: [guardId],
          description: `Leave: ${leaveType}`,
          created_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (error) throw error;

      toast.success(t('leaveShiftCreated') || 'Leave shift created successfully');
      queryClient.invalidateQueries({ queryKey: ['schedules-custom-range'] });
    } catch (error) {
      console.error('Error creating leave shift:', error);
      toast.error(t('errorCreatingLeaveShift') || 'Error creating leave shift');
    }
  };

  const handleDateRangeChange = ({ from, to }: { from: Date | undefined; to: Date | undefined }) => {
    if (from) {
      setDateRange([startOfDay(from), to ? endOfDay(to) : endOfDay(from)]);
    }
  };

  return (
    <Card className="w-full max-w-full">
      <CardHeader className="bg-gradient-to-r from-muted/50 to-background">
        <CardTitle className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <span className="text-lg font-bold text-primary">{t('customDateGuardView') || 'Custom Date Guard View'}</span>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative z-50">
              <DateRangePicker
                initialDateFrom={dateRange[0]}
                initialDateTo={dateRange[1]}
                onUpdate={handleDateRangeChange}
              >
                <Button variant="outline" size="sm" className="min-w-[200px] hover-scale">
                  <Calendar className="h-4 w-4 mr-2" />
                  {dateRange[0] && dateRange[1] 
                    ? `${format(dateRange[0], 'MMM dd')} - ${format(dateRange[1], 'MMM dd, yyyy')}`
                    : 'Select Date Range'
                  }
                </Button>
              </DateRangePicker>
            </div>
            <Button
              onClick={handleExportData}
              disabled={isExporting || guardsWithShifts.length === 0}
              variant="outline"
              size="sm"
              className="hover-scale"
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? (t('exporting') || 'Exporting...') : (t('export') || 'Export')}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 text-center">
            <div className="text-muted-foreground text-lg font-medium">
              {t('loading') || 'Loading...'}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden border rounded-lg max-w-full">
            {/* Header with dates */}
            <div 
              ref={headerRef}
              className="overflow-x-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-muted bg-gradient-to-r from-muted/50 to-muted/30"
              onScroll={handleHeaderScroll}
            >
              <div className="flex min-w-max">
                <div className="w-48 p-3 font-semibold border-r bg-background sticky left-0 z-10 text-primary shadow-sm">
                  {t('guard') || 'Guard'}
                </div>
                {selectedDays.map(day => (
                  <div key={day.toISOString()} className="w-32 p-3 text-center font-medium border-r hover:bg-primary/5 transition-colors">
                    <div className="text-xs font-medium text-primary">
                      {format(day, 'EEE')}
                    </div>
                    <div className="text-sm font-semibold">
                      {format(day, 'dd/MM')}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Content with guard schedules */}
            <div 
              ref={contentRef}
              className="overflow-x-auto max-h-96 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-muted"
              onScroll={handleContentScroll}
            >
              <div className="min-w-max">
                {guardsWithShifts.length > 0 ? (
                  guardsWithShifts.map((guard, index) => (
                    <div key={guard.id} className={`flex border-b hover:bg-primary/5 transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}>
                      <div className="w-48 p-3 font-medium border-r bg-background sticky left-0 z-10 shadow-sm">
                        <div className="text-sm font-semibold truncate">
                          {formatGuardName(guard)}
                        </div>
                      </div>
                       {selectedDays.map(day => {
                         const daySchedules = getGuardSchedulesForDay(guard.id, day);
                         return (
                           <ContextMenu key={day.toISOString()}>
                             <ContextMenuTrigger asChild>
                               <div className="w-32 p-2 border-r min-h-[80px] cursor-pointer hover:bg-primary/10 transition-all duration-200">
                                 <div className="space-y-1">
                                   {daySchedules.map(schedule => {
                                     const colors = generateEventColor(schedule.title, fromLocalISOString(schedule.start_date), theme);
                                     return (
                                       <div
                                         key={schedule.id}
                                         className="text-xs px-2 py-1 rounded-md truncate border shadow-sm hover:shadow-md transition-shadow"
                                         style={{
                                           backgroundColor: colors.backgroundColor,
                                           color: colors.textColor,
                                           borderColor: colors.textColor + '30'
                                         }}
                                         title={`${schedule.title} (${schedule.team_name})\n${(['REPO','ΡΕΠΟ'].includes(schedule.title)) ? '13:00 - 13:01' : (schedule.title.includes('ΑΔΕΙΑ') || schedule.title === 'ΜΗ ΕΡΓΑΣΙΑ' ? '14:01 - 22:00' : `${formatTime(schedule.start_date)} - ${formatTime(schedule.end_date)}`)}`}
                                        >
                                          <div className="font-medium truncate">{schedule.title}</div>
                                          <div className="text-xs opacity-75">
                                            {(['REPO','ΡΕΠΟ'].includes(schedule.title))
                                              ? '13:00 - 13:01'
                                              : (schedule.title.includes('ΑΔΕΙΑ') || schedule.title === 'ΜΗ ΕΡΓΑΣΙΑ'
                                                  ? '14:01 - 22:00'
                                                  : `${formatTime(schedule.start_date)} - ${formatTime(schedule.end_date)}`)
                                            }
                                          </div>
                                        </div>
                                     );
                                   })}
                                 </div>
                               </div>
                             </ContextMenuTrigger>
                             <ContextMenuContent>
                               <ContextMenuItem onClick={() => addRepoShift(guard.id, day)}>
                                 {t('addRepo') || 'Add Repo'}
                               </ContextMenuItem>
                               <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ')}>
                                 Add ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ
                               </ContextMenuItem>
                               <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ')}>
                                 Add ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ
                               </ContextMenuItem>
                               <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ')}>
                                 Add ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ
                               </ContextMenuItem>
                               <ContextMenuItem onClick={() => addLeaveShift(guard.id, day, 'ΜΗ ΕΡΓΑΣΙΑ')}>
                                 Add ΜΗ ΕΡΓΑΣΙΑ
                               </ContextMenuItem>
                             </ContextMenuContent>
                           </ContextMenu>
                         );
                       })}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <div className="text-muted-foreground text-lg font-medium">
                      {t('noShiftsAssigned') || 'No shifts assigned for the selected date range'}
                    </div>
                    <p className="text-sm text-muted-foreground/70 mt-2">
                      {t('selectDifferentRange') || 'Try selecting a different date range'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
